import clsx from 'clsx';
import type { ReactNode } from 'react';

export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'muted';

const tones: Record<Tone, string> = {
  neutral: 'bg-zinc-800 text-zinc-200 border-zinc-700',
  info: 'bg-sky-950/70 text-sky-300 border-sky-900',
  success: 'bg-emerald-950/70 text-emerald-300 border-emerald-900',
  warning: 'bg-amber-950/70 text-amber-300 border-amber-900',
  danger: 'bg-red-950/70 text-red-300 border-red-900',
  muted: 'bg-zinc-900 text-zinc-400 border-zinc-800',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={clsx(
        'inline-flex items-center whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const purchaseLabels: Record<string, { label: string; tone: Tone }> = {
  queued: { label: 'в очереди', tone: 'info' },
  processing: { label: 'в работе', tone: 'warning' },
  delivered: { label: 'выдан', tone: 'success' },
  failed: { label: 'ошибка', tone: 'danger' },
  canceled: { label: 'отменён', tone: 'muted' },
  refunded: { label: 'возврат', tone: 'warning' },
};

const numberLabels: Record<string, { label: string; tone: Tone }> = {
  pool: { label: 'в пуле', tone: 'info' },
  assigned: { label: 'выдан', tone: 'success' },
  expired: { label: 'истёк', tone: 'muted' },
  released: { label: 'освобождён', tone: 'muted' },
};

const batchLabels: Record<string, { label: string; tone: Tone }> = {
  open: { label: 'открыт', tone: 'info' },
  dispatching: { label: 'отправка', tone: 'warning' },
  done: { label: 'готов', tone: 'success' },
  failed: { label: 'ошибка', tone: 'danger' },
};

const txLabels: Record<string, { label: string; tone: Tone }> = {
  topup: { label: 'пополнение', tone: 'success' },
  charge: { label: 'списание', tone: 'neutral' },
  refund: { label: 'возврат', tone: 'warning' },
  adjustment: { label: 'корректировка', tone: 'info' },
};

const clientLabels: Record<string, { label: string; tone: Tone }> = {
  active: { label: 'активен', tone: 'success' },
  suspended: { label: 'заблокирован', tone: 'danger' },
};

const dictionaries = {
  purchase: purchaseLabels,
  number: numberLabels,
  batch: batchLabels,
  tx: txLabels,
  client: clientLabels,
} as const;

export function StatusBadge({
  kind,
  status,
}: {
  kind: keyof typeof dictionaries;
  status: string | null | undefined;
}) {
  if (!status) return <span className="text-zinc-500">—</span>;
  const entry = dictionaries[kind][status];
  return <Badge tone={entry?.tone ?? 'neutral'}>{entry?.label ?? status}</Badge>;
}

export const TYPE_LABELS: Record<number, string> = {
  1: 'SMS',
  2: 'аренда',
  3: 'спец.',
};

export function TypeBadge({ type }: { type: number | null | undefined }) {
  if (type === null || type === undefined) return <span className="text-zinc-500">—</span>;
  return <Badge tone="muted">{TYPE_LABELS[type] ?? `type ${type}`}</Badge>;
}
